import React, { useEffect, useState } from "react";
import { api } from "@/lib/auth";
import { toast } from "sonner";
import { GripVertical, Eye, EyeOff, ChevronUp, ChevronDown, Save } from "lucide-react";
import ImageUpload from "@/admin/ImageUpload";
import HomepageBatches from "@/admin/HomepageBatches";
import SectionHeadings from "@/admin/SectionHeadings";

export default function HomepageEditor() {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { load(); }, []);
  const load = async () => {
    const { data } = await api.get("/settings");
    setSettings(data);
  };

  const setField = (k, v) => setSettings(s => ({ ...s, [k]: v }));
  const toggleVisible = async (sec) => {
    const next = (settings.homepage_sections || []).map(x => x.key === sec.key ? { ...x, visible: !sec.visible } : x);
    setSettings(s => ({ ...s, homepage_sections: next }));
    try { await api.put("/admin/settings", { homepage_sections: next }); toast.success(sec.visible ? `${sec.label} hidden` : `${sec.label} shown`); }
    catch { toast.error("Could not save visibility"); load(); }
  };
  const move = (idx, dir) => {
    setSettings(s => {
      const arr = [...(s.homepage_sections || [])].sort((a,b) => (a.order||0)-(b.order||0));
      const to = idx + dir;
      if (to < 0 || to >= arr.length) return s;
      [arr[idx], arr[to]] = [arr[to], arr[idx]];
      return { ...s, homepage_sections: arr.map((x, i) => ({ ...x, order: i + 1 })) };
    });
  };

  const save = async () => {
    setSaving(true);
    try { await api.put("/admin/settings", settings); toast.success("Homepage saved"); }
    catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  if (!settings) return <div className="p-4 sm:p-8 text-zinc-500">Loading…</div>;
  const sections = (settings.homepage_sections || []).slice().sort((a,b) => (a.order||0)-(b.order||0));

  return (
    <div className="p-4 sm:p-8 max-w-[1200px]">
      <div className="flex justify-between items-center mb-6 flex-wrap gap-4">
        <div>
          <div className="eyebrow mb-2">Content</div>
          <h1 className="font-display text-5xl uppercase">Homepage editor</h1>
          <p className="text-zinc-400 mt-2 text-sm">Reorder, hide/show sections, edit every section heading, swap the hero image and pick featured batches.</p>
        </div>
        <button onClick={save} disabled={saving} className="btn-red flex items-center gap-2" data-testid="save-homepage">
          <Save size={14}/>{saving ? "Saving…" : "Save changes"}
        </button>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <div className="card-surface p-6">
          <div className="eyebrow mb-4">Hero</div>
          <label className="eyebrow block mb-1">Hero headline</label>
          <input value={settings.hero_headline || ""} onChange={e => setField("hero_headline", e.target.value)} className="w-full bg-black border border-white/10 px-3 py-2 text-sm mb-4 focus:border-red-600 outline-none" data-testid="hero-headline-input"/>
          <label className="eyebrow block mb-1">Hero sub-copy</label>
          <textarea value={settings.hero_sub || ""} onChange={e => setField("hero_sub", e.target.value)} className="w-full bg-black border border-white/10 px-3 py-2 text-sm mb-4 focus:border-red-600 outline-none" rows={3}/>
          <ImageUpload label="Hero background image" value={settings.hero_image} onChange={v => setField("hero_image", v)} testId="hero-image"/>
        </div>

        <div className="card-surface p-6">
          <div className="eyebrow mb-4">Section order & visibility</div>
          <div className="divide-y divide-white/5">
            {sections.map((sec, idx) => (
              <div key={sec.key} className="flex items-center gap-3 py-3" data-testid={`section-${sec.key}`}>
                <GripVertical size={14} className="text-zinc-600"/>
                <div className="flex-1">
                  <div className="font-semibold">{sec.label}</div>
                  <div className="text-xs text-zinc-500">{sec.key}</div>
                </div>
                <button onClick={() => move(idx, -1)} disabled={idx === 0} className="p-1 disabled:opacity-30 hover:text-red-500" data-testid={`section-${sec.key}-up`}><ChevronUp size={16}/></button>
                <button onClick={() => move(idx, 1)} disabled={idx === sections.length-1} className="p-1 disabled:opacity-30 hover:text-red-500" data-testid={`section-${sec.key}-down`}><ChevronDown size={16}/></button>
                <button onClick={() => toggleVisible(sec)} title={sec.visible ? "Hide on homepage" : "Show on homepage"} className={`p-1 ${sec.visible ? "text-emerald-500" : "text-zinc-600"}`} data-testid={`section-${sec.key}-toggle`}>
                  {sec.visible ? <Eye size={16}/> : <EyeOff size={16}/>}
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className="mt-6">
        <SectionHeadings settings={settings} setField={setField} />
      </div>
      <div className="mt-6">
        <HomepageBatches items={settings.homepage_batches} onChange={v => setField("homepage_batches", v)} />
      </div>
    </div>
  );
}
